import React, { useEffect, useState, useMemo } from 'react';
import Select from 'react-select';

// Define consistent styling variables (optional, but good for maintainability)
const BORDER_COLOR = '#D1D5DB'; // Tailwind gray-300
const BORDER_RADIUS = '0.375rem'; // Tailwind rounded-md
const INPUT_HEIGHT = '2.5rem'; // Tailwind h-10 (40px)
const FOCUS_RING_COLOR = '#4F46E5'; // Tailwind indigo-500 (example, use your brand's accent)
const PLACEHOLDER_COLOR = '#9CA3AF'; // Tailwind gray-400

const customStyles = {
    control: (provided, state) => ({
        ...provided,
        height: state.selectProps.isMulti ? 'auto' : INPUT_HEIGHT,
        minHeight: INPUT_HEIGHT,
        borderColor: state.isFocused ? FOCUS_RING_COLOR : BORDER_COLOR,
        borderRadius: BORDER_RADIUS,
        boxShadow: state.isFocused ? `0 0 0 1px ${FOCUS_RING_COLOR}` : 'none',
        '&:hover': {
            borderColor: state.isFocused ? FOCUS_RING_COLOR : BORDER_COLOR,
        },
        fontSize: '0.875rem',
    }),
    valueContainer: (provided, state) => ({
        ...provided,

        height: state.selectProps.isMulti ? 'auto' : INPUT_HEIGHT,
        maxHeight: state.selectProps.isMulti ? '120px' : INPUT_HEIGHT,
        overflowY: state.selectProps.isMulti ? 'auto' : 'hidden',
        padding: '2px 8px',
        alignItems: 'flex-start',
    }),
    input: (provided) => ({
        ...provided,
        margin: '0px',
        padding: '0px',
        height: `calc(${INPUT_HEIGHT} - 2px)`,
    }),
    placeholder: (provided) => ({
        ...provided,
        color: PLACEHOLDER_COLOR,
        fontSize: '0.875rem',
    }),
    singleValue: (provided) => ({
        ...provided,
        color: '#1F2937',
        fontSize: '0.875rem',
    }),
    multiValue: (provided) => ({
        ...provided,
        backgroundColor: '#E0E7FF',
        borderRadius: '0.25rem',
        display: 'flex',
        alignItems: 'center',
        margin: '2px',
    }),
    multiValueLabel: (provided) => ({
        ...provided,
        color: '#3730A3',
        fontSize: '0.875rem',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        paddingRight: '5px',
    }),
    dropdownIndicator: (provided) => ({
        ...provided,
        zIndex: 55,
        padding: '8px',
        color: '#6B7280',
    }),
    clearIndicator: (provided) => ({
        ...provided,
        zIndex: 55,
        padding: '8px',
        color: '#6B7280',
        '&:hover': {
            color: '#EF4444',
        },
    }),
    menu: (provided) => ({
        ...provided,
        zIndex: 50,
    }),
    menuPortal: (base) => ({
        ...base,
        zIndex: 9999,
    }),
};


// react-select's default getOptionLabel reads `option.label`, so a primitive
// defaultValue (e.g. a string restored from the URL) renders an empty chip / blank
// control. Normalize primitives to { value, label } option objects.
const toOption = (v) => (v !== null && v !== undefined && typeof v === 'object' ? v : { value: v, label: String(v) });

function Dropdown({ title, fetchUrl, options, onFilterSelect, resultKey, valueField, labelField, isMulti = false, defaultValue }) {
    const [selectOptions, setSelectOptions] = useState([]);
    const [optionsLoaded, setOptionsLoaded] = useState(false);

    const currentDate = useMemo(() => new Date(), []);

    // Resolve URL-restored default values against the real options (so the
    // correct labels show, e.g. "M" -> "Mon", "2511" -> "Fall 2025"). Until the
    // options arrive, fall back to the raw value as its own label.
    const defaultOptions = useMemo(() => {
        if (defaultValue === null || defaultValue === undefined) return undefined;
        const resolve = (v) => selectOptions.find(o => o.value === v) || toOption(v);
        const values = Array.isArray(defaultValue) ? defaultValue : [defaultValue];
        return isMulti ? values.map(resolve) : resolve(values[0]);
    }, [defaultValue, selectOptions, isMulti]);

    useEffect(() => {
        if (options) {

            const mappedOptions = options.map(opt => ({
                value: typeof opt === 'object' ? opt.value : opt,
                label: typeof opt === 'object' ? opt.label : opt,
            }));
            setSelectOptions(mappedOptions);
            if (mappedOptions.length > 0) setOptionsLoaded(true);

        } else if (fetchUrl) {
            fetch(fetchUrl)
                .then(response => {
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    return response.json();
                })
                .then(data => {
                    let items = [];
                    if (data?.data?.[resultKey]?.edges) { // For GraphQL-like structure
                        items = data.data[resultKey].edges.map(edge => edge.node);
                    } else if (data?.[resultKey] && Array.isArray(data[resultKey])) { // For direct array under resultKey
                        items = data[resultKey];
                    } else if (Array.isArray(data)) { // If data itself is the array
                        if (title === 'Term') {
                            items = data.filter(term => term.termDisplayControl === 'Y');
                        } else {
                            items = data;
                        }
                    } else {
                        console.warn(`Data for ${title} could not be parsed as expected from fetchUrl.`);
                        setSelectOptions([]);
                        return;
                    }


                    const getFieldValue = (fieldPath, dataNode) => {
                        if (!fieldPath || !dataNode) return undefined;
                        const fieldParts = fieldPath.split('.');
                        let fieldValue = dataNode;
                        for (const part of fieldParts) {
                            if (fieldValue === null || typeof fieldValue === 'undefined') return undefined;
                            if (part.includes('[')) {
                                const [arrayKey, arrayIndexStr] = part.split(/\[|\]/).filter(Boolean);
                                const index = parseInt(arrayIndexStr, 10);
                                fieldValue = fieldValue[arrayKey]?.[index];
                            } else {
                                fieldValue = fieldValue[part];
                            }
                        }
                        return fieldValue;
                    };

                    const results = items.map(node => {
                        const labelValue = getFieldValue(labelField, node);
                        const value = getFieldValue(valueField, node);

                        let formattedLabel = labelValue;

                        if (labelValue && typeof labelValue === 'string') {
                            formattedLabel = title === "Subjects" ? toTitleCase(labelValue) : labelValue;
                        }



                        return {
                            value: value,
                            label: formattedLabel || "N/A"
                        };
                    }).filter(result => result.value !== undefined && result.label !== "N/A");

                    let uniqueResults = Array.from(new Map(results.map(item => [item.label, item])).values());

                    setSelectOptions(uniqueResults);
                    if (uniqueResults.length > 0) setOptionsLoaded(true);
                })
                .catch(error => console.error(`Error fetching ${title}:`, error));
        }
    }, [fetchUrl, options, title, resultKey, valueField, labelField, currentDate]);

    const handleChange = (selectedOptions) => {
        if (isMulti) {
            onFilterSelect(selectedOptions ? selectedOptions.map(option => option.value) : []);
        } else {
            onFilterSelect(selectedOptions ? selectedOptions.value : null);
        }
    };

    return (

        <Select
            // react-select never re-resolves an uncontrolled defaultValue after
            // options load, so remount once when options arrive: that re-seeds
            // the value against the real options with their proper labels.
            key={defaultValue && optionsLoaded ? 'options-loaded' : 'initial'}
            isMulti={isMulti}
            name={title.toLowerCase().replace(/\s+/g, '-')}
            options={selectOptions}
            defaultValue={defaultOptions}

            onChange={handleChange}
            placeholder={options || fetchUrl ? `Select ${toTitleCase(title)}...` : `Loading ${toTitleCase(title)}...`}
            isClearable={!isMulti} // For single select
            isSearchable={true}
            styles={customStyles}
            menuPortalTarget={document.body}
            menuPosition="fixed"
        />
    );
}

function toTitleCase(str) {
    if (!str || typeof str !== 'string') return '';
    return str.toLowerCase().replace(/\b\w/g, function (char) {
        return char.toUpperCase();
    });
}

export default Dropdown;